/**
 * Base query key prefix for all billing-related queries
 */
export const billingQueryKeyPrefix = () => ['billing'] as const;

/**
 * Key for subscription status queries
 * @param installationId - GitHub App installation ID
 */
export const subscriptionStatusQueryKey = (installationId?: number | null) =>
  [...billingQueryKeyPrefix(), 'subscription-status', installationId] as const;

/**
 * Key for subscription seat status queries
 * @param installationId - GitHub App installation ID
 */
export const subscriptionSeatStatusQueryKey = (installationId?: number | null) =>
  [...billingQueryKeyPrefix(), 'subscription-seat-status', installationId] as const;

/**
 * Key for what paying for an organization's members waiting for a seat payment takes
 * @param installationId - GitHub App installation ID
 */
export const seatPaymentQueryKey = (installationId?: number | null) =>
  [...billingQueryKeyPrefix(), 'seat-payment', installationId] as const;

export const billingPaymentsQueryKey = (installationId?: number | null) =>
  [...billingQueryKeyPrefix(), 'payments', installationId] as const;

/**
 * Key for installation-scoped Codex rate limit usage queries
 * @param installationId - GitHub App installation ID
 * @param isImpersonatingSelectedInstallation - Whether the request uses super-admin installation impersonation
 * @param periodOffset - Billing period offset (0 = current, -1 = previous, ...)
 * @param includeBreakdown - Whether the payload carries the usage page breakdowns.
 *   Kept in the key so the lightweight banner payload and the full usage page
 *   payload never share a cache entry. Appended last so existing prefix-based
 *   invalidations (`[...prefix, 'codex-rate-limit-usage', installationId]`) still
 *   match both variants.
 */
export const codexRateLimitUsageQueryKey = (
  installationId?: number | null,
  isImpersonatingSelectedInstallation = false,
  periodOffset = 0,
  includeBreakdown = false,
) =>
  [
    ...billingQueryKeyPrefix(),
    'codex-rate-limit-usage',
    installationId,
    isImpersonatingSelectedInstallation,
    periodOffset,
    includeBreakdown,
  ] as const;

/**
 * Position of `periodOffset` inside `codexRateLimitUsageQueryKey`. Kept next to
 * the builder so a reordered key updates both in the same place.
 */
const CODEX_RATE_LIMIT_USAGE_PERIOD_OFFSET_KEY_INDEX = billingQueryKeyPrefix().length + 3;

/**
 * Whether two Codex usage query keys describe the same installation, impersonation
 * state, and breakdown selection, differing at most in the billing period. The
 * usage page reuses the previous period's payload as placeholder data only in
 * that case; a headline-only payload must never stand in for a breakdown payload.
 */
export function isSameCodexRateLimitUsageQueryIgnoringPeriod(
  previousKey: readonly unknown[] | undefined,
  nextKey: readonly unknown[],
): boolean {
  if (!previousKey || previousKey.length !== nextKey.length) {
    return false;
  }

  return previousKey.every(
    (segment, index) =>
      index === CODEX_RATE_LIMIT_USAGE_PERIOD_OFFSET_KEY_INDEX || segment === nextKey[index],
  );
}

export const recommendedPlanQueryKey = (installationId?: number | null, seats?: number | null) =>
  [...billingQueryKeyPrefix(), 'recommended-plan', installationId, seats] as const;

export const reviewLineLimitsQueryKey = (installationId?: number | null) =>
  [...billingQueryKeyPrefix(), 'review-line-limits', installationId] as const;
